<template>
  <b-row class="mb-4">
    <b-col cols="6">
      <b-card no-body class="card-content">
        <b-card-header
          class="d-flex align-items-center justify-content-between flex-wrap bg-white"
        >
          <h2 class="h3 font-weight-bold text-exitos mb-0 mr-sm-4">
            Consumo de traduções
            <span
              v-if="hasData"
              class="font-weight-normal text-muted text-uppercase text-sm"
              v-text="formattedPeriod"
            />
          </h2>

          <b-button
            type="button"
            variant="outline-exitos"
            class="btn-refresh d-inline-flex align-items-center justify-content-center mr-0 border-0 rounded-pill"
            aria-label="Atualizar consumo de traduções"
            :disabled="isFetching"
            :title="isFetching ? 'Atualizando consumo...' : 'Atualizar consumo'"
            @click="refetch"
          >
            <font-awesome-icon
              icon="rotate-right"
              aria-hidden="true"
              :spin="isFetching"
            />
          </b-button>
        </b-card-header>

        <b-skeleton-wrapper :loading="isInitialLoading">
          <template #loading>
            <b-row no-gutters class="align-items-center p-4">
              <b-col cols="12" md="8" class="d-flex align-items-center pr-5">
                <b-skeleton
                  type="avatar"
                  width="52px"
                  height="52px"
                  class="rounded-pill opacity-4"
                />
                <div class="flex-grow-1 ml-3">
                  <b-skeleton
                    width="48%"
                    height="24px"
                    class="rounded-sm opacity-4"
                  />
                  <b-skeleton
                    v-for="key in 2"
                    :key="'skeleton_' + key"
                    class="mt-3 rounded-sm"
                    :class="key % 2 === 0 ? 'opacity-3' : 'opacity-4'"
                    :width="key % 2 === 0 ? '60%' : '65%'"
                    height="16px"
                  />
                </div>
              </b-col>
            </b-row>
          </template>

          <template>
            <b-card-body class="px-3 px-md-4 pt-3 pb-4">
              <b-row no-gutters class="align-items-stretch" aria-live="polite">
                <b-col
                  cols="12"
                  :md="colsMdSize"
                  class="d-flex align-items-center pt-3 pb-2"
                >
                  <span
                    class="icon-avatar flex-shrink-0 aspect-1 d-inline-flex align-items-center justify-content-center rounded-lg mr-3"
                    :class="resume.iconClass"
                  >
                    <font-awesome-icon :icon="resume.icon" aria-hidden="true" />
                  </span>
                  <div>
                    <h3
                      class="h3 font-weight-bold text-default mb-0"
                      v-text="resume.title"
                    />
                    <p
                      class="font-weight-normal text-sm text-gray mb-0"
                      v-text="resume.description"
                    />
                  </div>
                </b-col>

                <b-col
                  v-if="existsPercentageLimit"
                  cols="12"
                  md="4"
                  class="d-flex flex-column align-items-start align-items-md-center justify-content-center mt-3 mt-md-0 py-4 pl-md-5"
                >
                  <span
                    class="h5 mb-0 font-weight-normal text-default font-italic"
                  >
                    Consumo do teto
                  </span>
                  <strong
                    class="h1 mt-0 font-weight-bold"
                    :class="resume.emphasisClass"
                    v-text="formattedPercentage"
                  />
                </b-col>
                <b-col v-else md="4">
                  <b-button
                    v-b-tooltip.hover
                    title="Definir limite"
                    variant="outline-exitos"
                    class="icon-to-config aspect-1 text-center px-0 border-0"
                    :to="{ name: 'assinaturas_configuracoes' }"
                  >
                    <font-awesome-icon icon="sliders" />
                  </b-button>
                </b-col>
              </b-row>
              <b-alert
                v-if="isError"
                show
                variant="warning"
                class="bg-warning bg-warning-5 text-warning d-inline-flex w-100 align-items-center"
              >
                <font-awesome-icon icon="info-circle" class="mr-1" />
                Não foi possível obter os dados mais recentes de consumo
                <b-button
                  class="ml-auto shadow-none"
                  size="sm"
                  variant="warning"
                  @click="refetch"
                >
                  <font-awesome-icon icon="arrows-rotate" class="mr-1" />
                  Recarregar
                </b-button>
              </b-alert>

              <vue-apex-charts
                v-else
                :options="chartOptionsConsume"
                :height="30"
                :series="chartDataConsume"
              />
            </b-card-body>
          </template>
        </b-skeleton-wrapper>
      </b-card>
    </b-col>

    <b-col cols="6">
      <b-card no-body class="card-content">
        <b-card-header class="bg-white">
          <h2 class="h3 font-weight-bold text-exitos mb-0 mr-sm-4">
            Consumo de traduções por idioma
          </h2>
        </b-card-header>
        <b-card-body>
          <b-alert
            v-if="isError"
            show
            variant="warning"
            class="bg-warning bg-warning-5 text-warning d-inline-flex w-100 align-items-center"
          >
            <font-awesome-icon icon="info-circle" class="mr-1" />
            Não foi possível obter os dados mais recentes de consumo
            <b-button
              class="ml-auto shadow-none"
              size="sm"
              variant="warning"
              @click="refetch"
            >
              <font-awesome-icon icon="arrows-rotate" class="mr-1" />
              Recarregar
            </b-button>
          </b-alert>
          <vue-apex-charts
            v-else
            :options="chartOptionsPerLanguage"
            width="100%"
            :height="130"
            :series="chartDatePerLanguage"
          />
        </b-card-body>
      </b-card>
    </b-col>

    <b-col cols="12" class="mb-4">
      <b-card no-body class="card-content mt-4">
        <b-card-header class="bg-white">
          <h2 class="h3 font-weight-bold text-exitos mb-0 mr-sm-4">
            Consumo de traduções por período
          </h2>
        </b-card-header>
        <b-card-body>
          <b-alert
            v-if="isError"
            show
            variant="warning"
            class="bg-warning bg-warning-5 text-warning d-inline-flex w-100 align-items-center"
          >
            <font-awesome-icon icon="info-circle" class="mr-1" />
            Não foi possível obter os dados mais recentes de consumo
            <b-button
              class="ml-auto shadow-none"
              size="sm"
              variant="warning"
              @click="refetch"
            >
              <font-awesome-icon icon="arrows-rotate" class="mr-1" />
              Recarregar
            </b-button>
          </b-alert>
          <vue-apex-charts
            v-else
            :options="chartOptionsPerPeriod"
            width="100%"
            :height="200"
            :series="chartDataPerPeriod"
          />
        </b-card-body>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import moment from "moment";
import { computed, defineComponent } from "vue";
import numberFormat from "@/helpers/filters/number_format.js";
import { formatNumber } from "@/helpers/functions/NumberHelpers.js";
import { useApexCharts } from "@/composables/useApexCharts.js";
import { useTraducaoConsumo } from "@/modules/central_consumo/composables/useTraducaoConsumo.js";
import { capitalize, toUpper } from "@/helpers/functions/StringHelpers";

export default defineComponent({
  name: "CentralConsumoPainel",
  setup() {
    const MAX_MONTHLY_CHARACTERS = 50_000_000;
    const FORMATS = {
      percentag: "0,000.[0]",
    };

    const { data, isFetching, isError, isPending, refetch } =
      useTraducaoConsumo();

    const hasData = computed(() => data.value !== null);

    const isInitialLoading = computed(() => isPending.value && !hasData.value);

    const percentage = computed(() => data.value?.percentual_do_teto ?? null);

    const existsPercentageLimit = computed(() => percentage.value !== null);

    const colsMdSize = computed(() => (existsPercentageLimit.value ? 8 : 12));

    const exceededPercentage = computed(() => percentage.value - 100);

    const chartDataConsume = computed(() => {
      const consumido = Number(data.value?.consumido_caracteres ?? 0);
      const projection = Number(data.value?.projecao_caracteres ?? 0);
      const limite = data.value?.teto_caracteres ?? null;
      const goals = [
        {
          name: "Projeção até o fim do período",
          value: projection,
          strokeColor: "#525f7f",
        },
      ];

      if (limite !== null) {
        goals.push({
          name: "Limite definido",
          value: Number(limite),
          strokeColor: "#dd4b39",
        });
      }

      return [
        {
          name: "Consumido",
          data: [
            {
              x: "Caracteres consumidos",
              y: consumido,
              goals,
            },
          ],
        },
      ];
    });

    const chartDataPerPeriod = computed(() => {
      const source = data.value?.por_periodo ?? {};
      return [
        {
          name: "Caracteres consumidos",
          data: Object.entries(source).map(([key, value]) => {
            const formattedDate = moment(key, "YYYY-MM");
            const label = capitalize(formattedDate.format("MMMM · YYYY"));

            return { y: Number(value), x: label };
          }),
        },
      ];
    });

    const chartDatePerLanguage = computed(() => {
      const result = [];

      const source = data.value?.por_idioma ?? {};

      for (const [key, value] of Object.entries(source)) {
        result.push({ x: toUpper(key), y: Number(value) });
      }

      return [{ name: "Caracteres consumidos", data: result }];
    });

    const { options: chartOptionsPerLanguage } = useApexCharts(
      computed(() => ({
        chart: {
          type: "bar",
        },
        plotOptions: {
          bar: {
            borderRadius: 3,
            barHeight: 18,
          },
        },
        grid: {
          padding: {
            bottom: 0,
            top: 0,
          },
        },
        dataLabels: {
          formatter: formatNumber,
        },
        legend: {
          show: false,
        },
        tooltip: {
          y: {
            formatter: (value) => {
              const total = Number(data.value?.consumido_caracteres ?? 0);
              const percentage = total > 0 ? (Number(value) / total) * 100 : 0;
              const formatted = numberFormat(percentage, FORMATS.percentag);

              return `${formatNumber(value)} (${formatted}%)`;
            },
          },
        },
      }))
    );

    const { options: chartOptionsConsume } = useApexCharts(
      computed(() => ({
        chart: {
          type: "bar",
          stacked: true,
          sparkline: {
            enabled: true,
          },
          parentHeightOffset: 0,
          toolbar: {
            show: false,
          },
          stackOnlyBar: true,
        },
        colors: ["#216eff", "#225373"],
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: false,
            borderRadius: 3,
            colors: {
              backgroundBarColors: ["rgba(34, 83, 115, 0.1)"],
            },
          },
        },
        dataLabels: {
          enabled: true,
          formatter: formatNumber,
        },
        grid: {
          show: false,
          padding: {
            bottom: 0,
            left: 0,
            right: 0,
            top: 0,
          },
        },
        tooltip: {
          y: {
            formatter: (value) => {
              const total = MAX_MONTHLY_CHARACTERS;
              const percentage = total > 0 ? (Number(value) / total) * 100 : 0;
              const formatted = numberFormat(percentage, FORMATS.percentag);

              return `${formatNumber(value)} (${formatted}%)`;
            },
          },
          shared: true,
          intersect: false,
          followCursor: false,
        },
        xaxis: {
          min: 0,
          max: MAX_MONTHLY_CHARACTERS,
          labels: {
            show: false,
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
        },
        yaxis: {
          labels: {
            show: false,
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
        },
      }))
    );

    const { options: chartOptionsPerPeriod } = useApexCharts(
      computed(() => ({
        chart: {
          type: "area",
        },
        tooltip: {
          y: {
            formatter: formatNumber,
          },
        },
        yaxis: {
          labels: {
            formatter: (val) => formatNumber(val),
          },
        },
      }))
    );

    const formattedPeriod = computed(() => {
      const period = data.value?.periodo;
      const date = moment(period, "YYYY-MM", true);
      const text = date.isValid() ? date.format("MMMM [de] YYYY") : period;

      return `· ${text}`;
    });

    const formattedPercentage = computed(() =>
      percentage.value === null
        ? ""
        : `${numberFormat(percentage.value, FORMATS.percentag)}%`
    );

    const resume = computed(() => {
      if (percentage.value === null) {
        return {
          icon: "info",
          iconClass: "text-exitos bg-exitos bg-exitos-10",
          title: "Limite não configurado",
          description: "O consumo está sendo registrado sem um limite mensal.",
        };
      }

      if (percentage.value > 100) {
        const percentual = numberFormat(
          exceededPercentage.value,
          FORMATS.percentag
        );
        return {
          icon: "exclamation",
          iconClass: "text-danger bg-danger bg-danger-10",
          emphasisClass: "text-danger",
          title: "Teto mensal ultrapassado",
          description: `O consumo ultrapassou o teto configurado em ${percentual}%.`,
        };
      }

      if (percentage.value === 100) {
        return {
          icon: "exclamation",
          iconClass: "text-danger bg-danger bg-danger-10",
          emphasisClass: "text-danger",
          title: "Teto mensal atingido",
          description: "O consumo alcançou o teto configurado.",
        };
      }

      if (percentage.value >= 90) {
        return {
          icon: "exclamation",
          iconClass: "text-danger bg-danger bg-danger-10",
          emphasisClass: "text-danger",
          title: "Consumo próximo do teto",
          description: "Restam menos de 10% do teto mensal.",
        };
      }

      if (percentage.value >= 80) {
        return {
          icon: "exclamation",
          iconClass: "text-warning bg-warning bg-warning-10",
          emphasisClass: "text-warning",
          title: "Consumo em atenção",
          description: "O consumo já ultrapassou 80% do teto mensal.",
        };
      }

      return {
        icon: "check",
        iconClass: "text-success-dark bg-success-dark bg-success-dark-10",
        emphasisClass: "text-exitos",
        title: "Consumo normal",
        description: "O teto mensal ainda não foi atingido.",
      };
    });

    return {
      existsPercentageLimit,
      formattedPercentage,
      formattedPeriod,
      hasData,
      isError,
      isFetching,
      isInitialLoading,
      resume,
      colsMdSize,
      chartDataConsume,
      chartOptionsConsume,
      chartOptionsPerLanguage,
      chartOptionsPerPeriod,
      chartDataPerPeriod,
      chartDatePerLanguage,
      refetch,
    };
  },
});
</script>

<style scoped lang="scss">
.icon-avatar {
  width: 2.5rem;
}
.card-content {
  height: 100%;
}

.icon-to-config {
  width: 2.5rem;
}

.btn-refresh {
  aspect-ratio: 1;
  width: 30px;
  padding: 0.5rem;
}
</style>
